import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../utils/axiosInstance";
import { supplierApi, getAuthHeaders } from "../config/api";

const managementHeaders = (getState, { multipart = false } = {}) => {
  const headers = getAuthHeaders(getState, { includeApiKey: true });
  if (multipart) {
    delete headers["Content-Type"];
  }
  return headers;
};

export const getAllSuppliers = createAsyncThunk(
  "suppliers/getAllSuppliers",
  async ({ page = 1, limit = 50, buyerId }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(
        `${supplierApi("/supplier/management")}?page=${page}&limit=${limit}&buyerId=${buyerId}`,
        { headers: managementHeaders(getState) }
      );
      return {
        suppliers: response.data.data,
        totalSuppliers: response.data.total,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error fetching suppliers");
    }
  }
);

export const getFilteredManagementSuppliers = createAsyncThunk(
  "suppliers/getFilteredManagementSuppliers",
  async ({ filters, page = 1, limit = 50, buyerId }, { rejectWithValue, getState }) => {
    try {
      let normalizedFilters = {};

      if (filters) {
        normalizedFilters = Object.entries(filters).reduce(
          (acc, [key, value]) => {
            if (key === "certification") {
              const certValue = Array.isArray(value) ? value[0] : value;

              acc["certificationStatus"] =
                certValue === "Certified"
                  ? true
                  : certValue === "Uncertified"
                  ? false
                  : undefined;
            } else {
              acc[key] = value;
            }

            return acc;
          },
          {}
        );
      }

      const response = await axios.post(
        `${supplierApi("/supplier/management/list")}?buyerId=${buyerId}&page=${page}&limit=${limit}`,
        normalizedFilters,
        { headers: managementHeaders(getState) }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getSuppliersByFolder = createAsyncThunk(
  "suppliers/getSuppliersByFolder",
  async (
    { folderName, page = 1, limit = 50, buyerId },
    { rejectWithValue, getState }
  ) => {
    try {
      const encodedFolder = encodeURIComponent(folderName);

      const response = await axios.get(
        `${supplierApi(`/supplier/management/folder/${encodedFolder}`)}?page=${page}&limit=${limit}&buyerId=${buyerId}`,
        { headers: managementHeaders(getState) }
      );

      return {
        folderName,
        suppliers: response.data.data,
        total: response.data.total,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch suppliers by folder"
      );
    }
  }
);

export const getFolderCountsByBuyerId = createAsyncThunk(
  "suppliers/getFolderCountsByBuyerId",
  async (buyerId, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(
        supplierApi("/supplier/management/folder-counts/name"),
        {
          headers: managementHeaders(getState),
          params: { buyerId },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch folder counts"
      );
    }
  }
);

export const createSupplier = createAsyncThunk(
  "suppliers/createSupplier",
  async (supplierInfo, { rejectWithValue, getState }) => {
    try {
      const response = await axios.post(
        supplierApi("/supplier/management/create"),
        supplierInfo,
        { headers: managementHeaders(getState) }
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error creating supplier");
    }
  }
);

export const uploadCSVWithMapping = createAsyncThunk(
  "suppliers/uploadCSVWithMapping",
  async ({ createdBy, file, map, addToFolder }, { rejectWithValue, getState }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("map", JSON.stringify(map));
      formData.append("createdBy", createdBy);
      formData.append("addToFolder", addToFolder);

      const response = await axios.post(
        supplierApi("/csv/upload"),
        formData,
        { headers: managementHeaders(getState, { multipart: true }) }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to upload CSV with mapping"
      );
    }
  }
);

export const editSupplierById = createAsyncThunk(
  "suppliers/editSupplierById",
  async ({ supplierId, updatedData }, { rejectWithValue, getState }) => {
    try {
      if (!supplierId) {
        throw new Error("Supplier ID is required");
      }
      const response = await axios.patch(
        supplierApi(`/supplier/management/${supplierId}`),
        updatedData,
        { headers: managementHeaders(getState) }
      );

      return response.data;
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data ||
        err.message ||
        "Error updating supplier";
      return rejectWithValue(message);
    }
  }
);

export const deleteSupplieById = createAsyncThunk(
  "suppliers/deleteSupplier",
  async ({ supplierId, buyerId }, { rejectWithValue, getState }) => {
    try {
      await axios.delete(
        `${supplierApi(`/supplier/management/${supplierId}`)}?buyerId=${buyerId}`,
        { headers: managementHeaders(getState) }
      );

      return { supplierId };
    } catch (err) {
      return rejectWithValue(err.response?.data || "Error deleting supplier");
    }
  }
);

export const bulkDeleteSuppliers = createAsyncThunk(
  "suppliers/bulkDeleteSuppliers",
  async ({ supplierIds, buyerId }, { rejectWithValue, getState }) => {
    try {
      await axios.delete(
        `${supplierApi("/supplier/management/bulk/delete")}?buyerId=${buyerId}`,
        {
          headers: managementHeaders(getState),
          data: { supplierIds },
        }
      );

      return { deletedIds: supplierIds };
    } catch (err) {
      return rejectWithValue(
        err.response?.data || "Error bulk deleting suppliers"
      );
    }
  }
);

export const sendRequestMessage = createAsyncThunk(
  "suppliers/sendRequestMessage",
  async ({ supplierId, message, buyerId }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.post(
        supplierApi(`/supplier/management/request-info/${supplierId}`),
        { message, buyerId },
        { headers: managementHeaders(getState) }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchPendingSuppliers = createAsyncThunk(
  "suppliers/fetchPendingSuppliers",
  async ({ buyerId }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(
        `${supplierApi("/supplier/management/status/pending")}?buyerId=${buyerId}`,
        { headers: managementHeaders(getState) }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const editFolderName = createAsyncThunk(
  "folders/editFolderName",
  async (
    { createdBy, folderToEdit, newFolderName, buyerId },
    { rejectWithValue, getState }
  ) => {
    try {
      const encodedFolderName = encodeURIComponent(folderToEdit);

      const response = await axios.patch(
        `${supplierApi(`/supplier/management/folder/${encodedFolderName}`)}?buyerId=${buyerId}`,
        { newFolderName },
        { headers: managementHeaders(getState) }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteFolder = createAsyncThunk(
  "folders/deleteFolder",
  async ({ createdBy, folderName, buyerId }, { rejectWithValue, getState }) => {
    try {
      const encodedFolderName = encodeURIComponent(folderName);
      const response = await axios.delete(
        supplierApi(
          `/supplier/management/delete/folder/${encodedFolderName}/buyer/${buyerId}`
        ),
        { headers: managementHeaders(getState) }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const sendInvite = createAsyncThunk(
  "supplier/sendInvite",
  async ({ supplierId, buyerId, message }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.post(
        supplierApi(`/supplier/management/send-invite/${supplierId}`),
        { message, buyerId },
        { headers: managementHeaders(getState) }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to send invite"
      );
    }
  }
);

export const moveSupplierToFolder = createAsyncThunk(
  "suppliers/moveSupplierToFolder",
  async (
    { supplierId, buyerId, newFolderName },
    { dispatch, rejectWithValue, getState }
  ) => {
    try {
      await axios.post(
        `${supplierApi(`/supplier/management/folder/${supplierId}`)}?buyerId=${buyerId}`,
        { newFolderName },
        { headers: managementHeaders(getState) }
      );

      await dispatch(getFolderCountsByBuyerId(buyerId));

      return { message: "Supplier moved successfully" };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const saveSupplierToBookmark = createAsyncThunk(
  "suppliers/saveSupplierToBookmark",
  async ({ buyerId, supplierId }, { rejectWithValue, getState }) => {
    try {
      const res = await axios.post(
        supplierApi("/v1/suppliers/save-supplier"),
        { buyerId, supplierId },
        { headers: managementHeaders(getState) }
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const getSavedSuppliers = createAsyncThunk(
  "suppliers/getSavedSuppliers",
  async (buyerId, { rejectWithValue, getState }) => {
    try {
      const res = await axios.get(
        supplierApi(`/v1/suppliers/save-supplier/${buyerId}`),
        { headers: managementHeaders(getState) }
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const deleteSavedSupplier = createAsyncThunk(
  "suppliers/deleteSavedSupplier",
  async ({ buyerId, supplierId }, { rejectWithValue, getState }) => {
    try {
      const res = await axios.delete(
        supplierApi(`/v1/suppliers/save-supplier/${buyerId}/${supplierId}`),
        { headers: managementHeaders(getState) }
      );
      return { supplierId, message: res.data?.message };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

function countSuppliersByFolder(suppliers, folderName, total) {
  if (folderName === "Total Suppliers") return total;
  return suppliers?.filter((s) => s.folder?.name === folderName).length;
}

const folders = [
  { name: "Total Suppliers", numberOfSuppliers: 0, default: true },
  { name: "Tier 1", numberOfSuppliers: 0, default: true },
  { name: "Tier 2", numberOfSuppliers: 0, default: true },
  { name: "Tier 3", numberOfSuppliers: 0, default: true },
];

const initialState = {
  suppliers: [],
  filteredSuppliers: [],
  suppliersByFolder: [],
  pendingSuppliers: [],
  totalSuppliers: 0,
  totalPendingSuppliers: 0,
  saved: [],
  folders: folders,
  loading: false,
  success: false,
  error: null,
  messageStatus: null,
};

const suppliersSlice = createSlice({
  name: "suppliers",
  initialState,
  reducers: {
    addSuppliers: (state, action) => {
      const newSuppliers = action.payload;
      state.suppliers.push(...newSuppliers);
    },

    updateSupplier: (state, action) => {
      const updatedSupplier = action.payload;

      state.suppliers = state.suppliers.map((supplier) =>
        supplier._id === updatedSupplier._id ? updatedSupplier : supplier
      );

      state.folders = state.folders.map((folder) => ({
        ...folder,
        numberOfSuppliers: countSuppliersByFolder(
          state.suppliers,
          folder.name,
          state.totalSuppliers
        ),
      }));
    },

    deleteSupplier: (state, action) => {
      const idToDelete = action.payload._id;
      const folderName = action.payload.folder?.name;
      const wasDeleted = state.suppliers.some(
        (supplier) => supplier._id === idToDelete
      );

      state.suppliers = state.suppliers.filter(
        (supplier) => supplier._id !== idToDelete
      );
      state.suppliersByFolder = state.suppliersByFolder.filter(
        (supplier) => supplier._id !== idToDelete
      );

      if (wasDeleted) {
        state.folders = state.folders.map((folder) => {
          if (folder.name === "Total Suppliers" || !folderName) {
            return {
              ...folder,
              numberOfSuppliers: Math.max(
                (folder.numberOfSuppliers || 0) - 1,
                0
              ),
            };
          }

          if (folder.name === folderName) {
            return {
              ...folder,
              numberOfSuppliers: Math.max(
                (folder.numberOfSuppliers || 0) - 1,
                0
              ),
            };
          }

          return folder;
        });
      }
    },

    setFolders: (state, action) => {
      state.folders = action.payload;
    },

    updateFolders: (state, action) => {
      const folderUpdates = action.payload;
      state.folders = state.folders.map((folder) => {
        const match = folderUpdates.find((f) => f.name === folder.name);
        return match ? { ...folder, ...match } : folder;
      });
    },
    updateFolderCounts: (state, action) => {
      const { folderName, count } = action.payload;
      state.folders = state.folders.map((folder) => {
        const isTargetFolder = folder.name === folderName;
        const isTotalFolder = folder.name === "Total Suppliers";

        if (isTargetFolder || isTotalFolder) {
          return {
            ...folder,
            numberOfSuppliers: (folder.numberOfSuppliers || 0) + count,
          };
        }

        return folder;
      });
    },
    setSuppliers: (state, action) => {
      state.suppliers = action.payload;
    },

    removeSavedSupplier: (state, action) => {
      const supplierId = action.payload;
      state.saved = state.saved.filter((s) => s._id !== supplierId);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllSuppliers.fulfilled, (state, action) => {
        const { suppliers, totalSuppliers } = action.payload;

        state.suppliers = suppliers;
        state.totalSuppliers = totalSuppliers;
        state.loading = false;
        state.error = null;
      })
      .addCase(saveSupplierToBookmark.fulfilled, (state, action) => {
        state.loading = false;
        const newSupplier = action.payload;
        const exists = state.saved.find((s) => s._id === newSupplier._id);
        if (!exists) state.saved.push(newSupplier);
      })
      .addCase(getSavedSuppliers.fulfilled, (state, action) => {
        state.loading = false;
        state.saved = action.payload;
      })
      .addCase(deleteSavedSupplier.fulfilled, (state, action) => {
        state.loading = false;
        state.saved = state.saved.filter(
          (s) => s._id !== action.payload.supplierId
        );
      })
      .addCase(getFilteredManagementSuppliers.fulfilled, (state, action) => {
        state.filteredSuppliers = action.payload.data.suppliers;

        state.loading = false;
        state.error = null;
      })
      .addCase(getSuppliersByFolder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSuppliersByFolder.fulfilled, (state, action) => {
        const { folderName, suppliers } = action.payload;
        state.suppliersByFolder = suppliers;
        state.selectedFolder = folderName;
        state.loading = false;
      })
      .addCase(getSuppliersByFolder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error loading folder suppliers";
      })

      .addCase(createSupplier.pending, (state) => {
        state.createStatus = { loading: true, success: false, error: null };
      })
      .addCase(createSupplier.fulfilled, (state, action) => {
        const newSupplier = action.payload;
        state.suppliers.push(newSupplier);

        state.createStatus = { loading: false, success: true, error: null };
      })
      .addCase(createSupplier.rejected, (state, action) => {
        state.createStatus = {
          loading: false,
          success: false,
          error: action.payload || "Unknown error",
        };
      })
      .addCase(getFolderCountsByBuyerId.fulfilled, (state, action) => {
        state.loading = false;

        const updatedCounts = action.payload;
        const defaultFolders = ["Total Suppliers", "Tier 1", "Tier 2"];

        const dynamicFolders = updatedCounts.map((f) => ({
          name: f.folderName,
          numberOfSuppliers: f.total,
          default: defaultFolders.includes(f.folderName),
        }));

        const mergedFolders = [
          {
            name: "Total Suppliers",
            numberOfSuppliers: state.totalSuppliers,
            default: true,
          },
          ...["Tier 1", "Tier 2"].map((tier) => {
            const match = updatedCounts.find((f) => f.folderName === tier);
            return {
              name: tier,
              numberOfSuppliers: match ? match.total : 0,
              default: true,
            };
          }),
          ...dynamicFolders.filter((f) => !defaultFolders.includes(f.name)),
        ];

        state.folders = mergedFolders;
      })

      .addCase(editSupplierById.pending, (state) => {
        state.updateStatus = { loading: true, success: false, error: null };
      })
      .addCase(editSupplierById.fulfilled, (state, action) => {
        const updatedSupplier = action.payload;
        const updatedId = String(
          updatedSupplier?._id || updatedSupplier?.id || ""
        );

        if (updatedId) {
          state.suppliers = state.suppliers.map((supplier) =>
            String(supplier._id) === updatedId ? { ...supplier, ...updatedSupplier } : supplier
          );
        }

        state.updateStatus = { loading: false, success: true, error: null };
      })
      .addCase(editSupplierById.rejected, (state, action) => {
        state.updateStatus = {
          loading: false,
          success: false,
          error: action.payload || "Unknown error",
        };
      })
      .addCase(bulkDeleteSuppliers.fulfilled, (state, action) => {
        const { deletedIds } = action.payload;

        const deletedSuppliers = state.suppliers.filter((s) =>
          deletedIds.includes(s._id)
        );

        state.suppliers = state.suppliers.filter(
          (s) => !deletedIds.includes(s._id)
        );

        state.folders = state.folders.map((folder) => {
          const countReduction = deletedSuppliers.filter(
            (s) =>
              folder.name === "Total Suppliers" ||
              folder.name === s.folder?.name
          ).length;

          return {
            ...folder,
            numberOfSuppliers: Math.max(
              (folder.numberOfSuppliers || 0) - countReduction,
              0
            ),
          };
        });
      })

      .addCase(bulkDeleteSuppliers.rejected, (state, action) => {
        state.error = action.payload || "Failed to bulk delete suppliers";
      })
      .addCase(sendRequestMessage.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.messageStatus = null;
      })
      .addCase(sendRequestMessage.fulfilled, (state, action) => {
        state.loading = false;
        state.messageStatus = "success";
      })
      .addCase(sendRequestMessage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.messageStatus = "error";
      })
      .addCase(fetchPendingSuppliers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPendingSuppliers.fulfilled, (state, action) => {
        state.loading = false;
        state.pendingSuppliers = action.payload.data;
        state.totalPendingSuppliers = action.payload.total;
      })
      .addCase(fetchPendingSuppliers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(editFolderName.pending, (state) => {
        state.loading = true;
      })
      .addCase(editFolderName.fulfilled, (state, action) => {
        state.loading = false;

        const { folderToEdit, newFolderName } = action.meta.arg;

        const folderIndex = state.folders.findIndex(
          (folder) => folder.name === folderToEdit
        );

        if (folderIndex !== -1) {
          state.folders[folderIndex].name = newFolderName;
        }
      })
      .addCase(editFolderName.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(deleteFolder.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteFolder.fulfilled, (state, action) => {
        state.loading = false;

        const { folderName } = action.meta.arg;

        state.folders = state.folders.filter(
          (folder) => folder.name !== folderName
        );
      })
      .addCase(deleteFolder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  addSuppliers,
  updateSupplier,
  deleteSupplier,
  setFolders,
  updateFolders,
  updateFolderCounts,
  removeSavedSupplier,
} = suppliersSlice.actions;

export default suppliersSlice.reducer;
